import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Wallet, TrendingUp, CheckCircle2, Receipt, Percent, Download } from "lucide-react";
import { toast } from "sonner";

export default function Pay() {
  const { user } = useAuth();
  const isPro = user?.role === "professional";
  const [data, setData] = useState(null);

  useEffect(() => {
    if (isPro) api.get("/me/earnings").then((r) => setData(r.data)).catch(() => setData(false));
    else api.get("/payments").then((r) => setData({ payments: r.data.payments })).catch(() => setData(false));
  }, [isPro]);

  if (data === null) return <Loader label="Loading Krida Pay" />;
  if (!data) return <Empty label="Could not load payments" />;

  const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
  const payments = data.payments || [];
  const s = data.summary;

  return (
    <div className="fade-up">
      <PageHeader overline="Payments & earnings" title="Krida Pay"
        subtitle={isPro ? "Match fees, settlements and invoices — settled through your CYRA Work Wallet." : "Workforce payments, platform fees and settlements — via the CYRA Work Wallet."} testid="krida-pay-page" />

      {isPro && s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total earnings" value={inr(s.total)} icon={Wallet} accent="emerald" testid="pay-total" />
          <StatCard label="Available" value={inr(s.available)} icon={CheckCircle2} accent="cobalt" />
          <StatCard label="Pending" value={inr(s.pending)} icon={TrendingUp} accent="amber" />
          <StatCard label="Platform fees" value={inr(s.platform_fees)} icon={Percent} accent="crimson" />
        </div>
      )}

      <div className="krida-card p-6">
        <div className="flex items-center gap-2 mb-4"><Receipt className="w-5 h-5 text-krida-emerald" /><h3 className="font-heading text-xl font-bold">{isPro ? "Payment history" : "Settlements"}</h3></div>
        {payments.length === 0 ? <Empty label="No payments yet" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left overline text-slate-500 border-b border-white/5">
                <th className="pb-3 font-mono">Invoice</th><th className="pb-3 font-mono">{isPro ? "From" : "Professional"}</th>
                <th className="pb-3 font-mono">Type</th><th className="pb-3 font-mono text-right">Gross</th>
                <th className="pb-3 font-mono text-right">Fee</th><th className="pb-3 font-mono text-right">Net</th>
                <th className="pb-3 font-mono">Status</th><th className="pb-3"></th>
              </tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`payment-${p.id}`}>
                    <td className="py-3 font-mono text-xs text-slate-400">{p.invoice_no}</td>
                    <td className="py-3">{isPro ? p.org_name : p.professional_name}</td>
                    <td className="py-3 text-slate-400">{p.type}</td>
                    <td className="py-3 text-right font-mono">{inr(p.gross)}</td>
                    <td className="py-3 text-right font-mono text-krida-crimson">-{inr(p.platform_fee)}</td>
                    <td className="py-3 text-right font-mono text-krida-emerald font-semibold">{inr(p.net)}</td>
                    <td className="py-3"><Tag tone={statusTone(p.status)}>{p.status}</Tag></td>
                    <td className="py-3 text-right"><button onClick={() => toast.success(`Invoice ${p.invoice_no} downloaded`)} aria-label="Download invoice" data-testid={`pay-invoice-download-btn-${p.id}`} className="text-slate-500 hover:text-krida-crimson"><Download className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
